import React, { useEffect, useMemo, useRef } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const useStyles = makeStyles(() => ({
    chart: {
        width: '100%',
        height: 360,
    },
}));

const costFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
});

export default function ConsumptionHistoryChart({ rows, groups, metric = 'cost', unit = '', chartId = 'billing-consumption-history-chart' }) {
    const classes = useStyles();
    const theme = useTheme();
    const fontFamily = theme.typography?.fontFamily || 'sans-serif';
    const rootRef = useRef(null);

    const chartData = useMemo(() => (
        rows.map(row => {
            const item = {
                period: row.label,
                totalCost: row.totalCost,
                totalConsumption: row.totalConsumption,
                totalConsumptionLabel: row.unitLabel ? `${row.totalConsumption} ${row.unitLabel}` : `${row.totalConsumption}`,
            };

            row.segments.forEach(segment => {
                item[segment.id] = segment.costo || 0;
                item[`${segment.id}CostLabel`] = costFormatter.format(segment.costo || 0);
                item[`${segment.id}Consumption`] = segment.consumo || 0;
                item[`${segment.id}Unit`] = segment.unitLabel || '';
                item[`${segment.id}ConsumptionLabel`] = segment.unitLabel
                    ? `${segment.consumo || 0} ${segment.unitLabel}`
                    : `${segment.consumo || 0}`;
            });

            return item;
        })
    ), [rows]);

    useEffect(() => {
        if (rootRef.current) {
            rootRef.current.dispose();
            rootRef.current = null;
        }

        if (!chartData.length) {
            return undefined;
        }

        const isCost = metric === 'cost';

        const root = am5.Root.new(chartId);
        rootRef.current = root;
        const projectTheme = am5.Theme.new(root);

        projectTheme.rule('Label').setAll({
            fontFamily,
        });

        projectTheme.rule('Tooltip').setAll({
            fontFamily,
        });

        root.setThemes([am5themes_Animated.new(root), projectTheme]);

        if (root._logo) {
            root._logo.dispose();
        }

        root.numberFormatter.setAll({
            numberFormat: isCost ? '$ #,###.##' : '#,###.##',
            numericFields: ['valueY'],
        });

        const chart = root.container.children.push(am5xy.XYChart.new(root, {
            panX: false,
            panY: false,
            wheelX: 'none',
            wheelY: 'none',
            layout: root.verticalLayout,
            paddingTop: 8,
            paddingRight: 12,
            paddingLeft: 0,
        }));

        const categoryAxisRenderer = am5xy.AxisRendererX.new(root, {
            minGridDistance: 28,
            cellStartLocation: 0.21,
            cellEndLocation: 0.79,
        });

        categoryAxisRenderer.grid.template.setAll({
            location: 0,
            strokeOpacity: 0.08,
        });

        categoryAxisRenderer.labels.template.setAll({
            fontSize: 12,
            fill: am5.color('#4b5563'),
        });

        const categoryAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
            categoryField: 'period',
            renderer: categoryAxisRenderer,
        }));

        categoryAxis.data.setAll(chartData);

        const valueAxisRenderer = am5xy.AxisRendererY.new(root, {
            minGridDistance: 50,
        });

        valueAxisRenderer.grid.template.setAll({
            strokeOpacity: 0.12,
        });

        valueAxisRenderer.labels.template.setAll({
            fontSize: 12,
            fill: am5.color('#4b5563'),
        });

        const consumptionFormat = unit ? `#,###.##' ${unit}'` : '#,###.##';

        const valueAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            extraMax: 0.08,
            renderer: valueAxisRenderer,
            numberFormat: isCost ? '$ #,###.##' : consumptionFormat,
        }));

        groups.forEach((group, index) => {
            const isLast = index === groups.length - 1;

            const series = chart.series.push(am5xy.ColumnSeries.new(root, {
                name: group.name,
                xAxis: categoryAxis,
                yAxis: valueAxis,
                valueYField: isCost ? group.id : `${group.id}Consumption`,
                categoryXField: 'period',
                stacked: true,
            }));

            const tooltipText = isCost
                ? `[bold]{name}[/]\nPeríodo: {categoryX}\nCosto: [bold]{valueY.formatNumber('$ #,###.##')}[/]\nConsumo: [bold]{${group.id}ConsumptionLabel}[/]`
                : `[bold]{name}[/]\nPeríodo: {categoryX}\nConsumo: [bold]{valueY.formatNumber('#,###.##')} {${group.id}Unit}[/]\nCosto: [bold]{${group.id}CostLabel}[/]`;

            series.columns.template.setAll({
                width: am5.percent(62),
                tooltipText,
                fill: am5.color(group.color),
                stroke: am5.color(group.color),
                strokeOpacity: 1,
                cornerRadiusTL: isLast ? 4 : 0,
                cornerRadiusTR: isLast ? 4 : 0,
                cornerRadiusBL: 0,
                cornerRadiusBR: 0,
            });

            series.data.setAll(chartData);
        });

        const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {
            behavior: 'none',
        }));
        cursor.lineX.set('visible', false);
        cursor.lineY.set('visible', false);

        const legend = chart.children.push(am5.Legend.new(root, {
            centerX: am5.percent(50),
            x: am5.percent(50),
            marginTop: 16,
        }));

        legend.labels.template.setAll({
            fontSize: 12,
            fill: am5.color('#4b5563'),
        });

        legend.markers.template.setAll({
            width: 10,
            height: 10,
        });

        legend.data.setAll(chart.series.values);

        return () => {
            root.dispose();
            rootRef.current = null;
        };
    }, [chartData, chartId, fontFamily, groups, metric, unit]);

    return <Box id={chartId} className={classes.chart} />;
}
